import { createComputed, getOwner, Owner, runWithOwner } from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import { Location, useLocation, useNavigate } from "@solidjs/router";

import AsyncBatcher from "@repo/utils/AsyncBatcher";
import EventEmitter from "@repo/utils/EventEmitter";
import { Abort } from "@repo/utils/EventEmitter";
import Log from "@repo/utils/Log";
import UArray from "@repo/utils/UArray";
import UFunction from "@repo/utils/UFunction";
import UObject from "@repo/utils/UObject";
import UPath from "@repo/utils/UPath";
import UProxy from "@repo/utils/UProxy";
import USearchParams from "@repo/utils/USearchParams";
import UType from "@repo/utils/UType";
import Platform from "@repo/utils-client/Platform";

import USolid from "./USolid";

if (Platform.isClient)
  window.history.replaceState({ "AppRouter:id": crypto.randomUUID() }, "");

const nestedQuery = UFunction.memo(
  (
    search: string,
  ): UType.Nested<Record<string, UType.Nested.Children | string>> => {
    const res = {};
    for (const [key, value] of new URLSearchParams(search)) {
      UObject.mut.setByPath.createParents(res, key.split("."), value);
    }
    return res;
  },
);

export declare namespace useRouter {
  type Options = {
    external?: (path: string) => boolean;
  };
}

export function useRouter(options?: useRouter.Options) {
  const location = useLocation<{ "AppRouter:id": string }>();
  const navigate = useNavigate();

  const idHistory = Platform.isClient
    ? [location.state?.["AppRouter:id"]!]
    : [];
  const currentHistoryIndex = () =>
    idHistory.indexOf(location.state?.["AppRouter:id"]!);
  const [locationById, setLocationById] = createStore<Record<string, Location>>(
    {},
  );
  createComputed(() => {
    if (location.state?.["AppRouter:id"])
      setLocationById(location.state?.["AppRouter:id"]!, reconcile(location));
  });

  const events = new EventEmitter<
    [
      ["push", string, Abort | void],
      ["replace", string, Abort | void],
      ["back", undefined, Abort | void],
      ["back.or", ".." | (string & {}), Abort | void],
    ]
  >();

  const queryBuffer = new AsyncBatcher(
    (
      buffer: (
        | [operation: "set", kv: [key: string, value: string]]
        | [operation: "delete", key: string]
      )[],
    ) => {
      const url = new URL(window.location.origin);
      url.pathname = location.pathname;
      url.hash = location.hash;
      url.search = (() => {
        const params = new URLSearchParams(location.search);
        for (const [operation, payload] of buffer) {
          if (operation === "set") params.set(...payload);
          if (operation === "delete") params.delete(payload);
        }
        return params.toString();
      })();

      if (url.search === location.search) return Promise.resolve();

      navigate(UPath.resolve(url.href, location.pathname), { replace: true });

      return USolid.Accessor.onUpdate(() => {
        ({ ...location });
      });
    },
  );

  const router = UProxy.merge(
    UProxy.merge(location, {
      on: events.on,
      get nestedQuery() {
        return nestedQuery(location.search);
      },
      history: {
        get backSize() {
          return currentHistoryIndex();
        },
        get forwardSize() {
          return idHistory.length - currentHistoryIndex() - 1;
        },
        peekRelative: (relative: number) =>
          locationById[idHistory[currentHistoryIndex() + relative]!],
      },
    }),
    UProxy.deepShim(
      (() => {
        const change = (path: string, offset: number) => {
          const url = new URL(path, "path://");
          const id = crypto.randomUUID();

          const state = { "AppRouter:id": id };
          setLocationById(id, {
            pathname: url.pathname,
            search: url.search,
            hash: url.hash,
            state,
            query: USearchParams.Object.denormalize(
              USearchParams.toObject(url.searchParams),
            ),
            key: "",
          });

          idHistory.splice(
            currentHistoryIndex() + offset,
            router.history.forwardSize + 1 - offset,
            id,
          );

          return { state };
        };

        return {
          push(path: string) {
            const resolved = UPath.resolve(path, location.pathname);
            if (options?.external?.(resolved))
              window.location.pathname = resolved;
            else {
              const { state } = change(resolved, 1);
              navigate(resolved, { state });
            }
          },
          replace(path: string) {
            const resolved = UPath.resolve(path, location.pathname);
            if (options?.external?.(resolved))
              window.location.replace(resolved);
            else {
              const { state } = change(resolved, 0);
              navigate(resolved, { replace: true, state });
            }
          },
          addQuery: (path: UArray.Maybe<string>, value: string) =>
            queryBuffer.push(["set", [UArray.ensure(path).join("."), value]]),
          removeQuery: (path: UArray.Maybe<string>) =>
            queryBuffer.push(["delete", UArray.ensure(path).join(".")]),
          back: Object.assign(
            () => {
              navigate(-1);
            },
            {
              or(defaultRoute: ".." | (string & {})) {
                if (window.history.length > 1) router.back();
                else router.replace(defaultRoute);
              },
            },
          ),
        };
      })(),
      (original, path) =>
        (...args) => {
          // @ts-expect-error
          const e = events.emit(path.join("."), args[0]);
          const aborted = e.results.some((res) => res instanceof Abort);

          Log.withTrace`${
            aborted ? Log.style("background: #f00", "Aborted") : ""
          }${aborted ? " " : ""}${Log.fn(
            [Log.color("#8f8", "AppRouter"), ...path],
            args,
          )}`;

          if (!aborted) return original(...args);
        },
    ),
  );

  return router;
}

type AppRouter = AppRouter.Context & {
  external: useRouter.Options["external"];
  registered: boolean;
  register(options?: {
    owner?: Owner | null;
    external?: useRouter.Options["external"];
  }): void;
};
declare namespace AppRouter {
  type Context = ReturnType<typeof useRouter>;
}

let registered: AppRouter.Context;
let external: useRouter.Options["external"];

const AppRouter: AppRouter = new Proxy(
  {
    get external() {
      return external;
    },
    get registered() {
      return !!registered;
    },
    register({ owner = getOwner(), external: _external } = {}) {
      if (!owner)
        throw new Error("AppRouter must be registered in a Router context.");
      runWithOwner(owner, () => {
        external = _external;
        registered = useRouter({ external: _external });
      });
    },
  } as AppRouter,
  {
    get(target: any, p) {
      if (p in target) return target[p];
      if (!registered)
        throw new Error("Attempted to use Router before registration.");
      // Rename to Router to make errors clearer
      const AppRouter: any = registered;
      return AppRouter[p];
    },
  },
);

export default AppRouter;
